/**
 * Каталог навыков агентов (/skills).
 *
 * Данные собраны на этапе сборки из docs/skills (см. scripts/sync-skills.mjs),
 * поэтому страница без загрузок и ошибок — просто сетка карточек, как в
 * каталоге моделей.
 *
 * У каждой карточки есть копирование самого SKILL.md: агенту навык часто не
 * ставят файлом, а вставляют текстом прямо в диалог, и ради этого не должно
 * приходиться открывать страницу навыка и сырой файл за ней.
 */
import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { skillPath } from "../app/routes";
import { loadSkillMarkdown, SKILLS } from "../app/skills";
import { useI18n } from "../i18n/I18nProvider";
import { Badge, Button, Card, CopyIcon, Tile, typographyClass, useToast } from "../ui-kit";

export function SkillsPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const toast = useToast();
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Уже загруженные тексты. Ленивый чанк остаётся ленивым, но второй раз за
  // ним не ходим — и, главное, из кэша `writeText` вызывается в том же тике,
  // что и клик.
  const loaded = useRef(new Map<string, string>());

  /**
   * Прогреть чанк навыка заранее — по наведению и по фокусу.
   *
   * Firefox и Safari требуют, чтобы `writeText` вызывался, пока жива
   * транзиентная активация от клика; загрузка чанка её съедает, и копирование
   * падает с `NotAllowedError`. Прогрев делает клик синхронным.
   */
  const prefetchMarkdown = (id: string) => {
    if (loaded.current.has(id)) return;
    void loadSkillMarkdown(id)
      .then((markdown) => loaded.current.set(id, markdown))
      // Молча: человек ничего не просил, а ошибку он увидит по клику.
      .catch(() => undefined);
  };

  /** Текст навыка в буфер. Сам markdown лежит в ленивом чанке — грузим по клику. */
  const copyMarkdown = (id: string) => {
    // Без явной проверки `writeText` на http-стенде не существует, а
    // `await undefined` спокойно доходит до галочки — человек уверен, что
    // скопировал, и вставляет пустоту.
    if (!navigator.clipboard) {
      toast.show({ title: t("skills.copyFailed"), tone: "error" });
      return;
    }
    const cached = loaded.current.get(id);
    const written =
      cached !== undefined
        ? navigator.clipboard.writeText(cached)
        : loadSkillMarkdown(id).then((markdown) => {
            loaded.current.set(id, markdown);
            return navigator.clipboard.writeText(markdown);
          });
    void written
      .then(() => {
        setCopiedId(id);
        window.setTimeout(
          () => setCopiedId((current) => (current === id ? null : current)),
          1500,
        );
      })
      .catch(() => toast.show({ title: t("skills.copyFailed"), tone: "error" }));
  };

  return (
    <Card>
      <div>
        <h1 className={typographyClass("header-24-semibold")}>{t("skills.title")}</h1>
        <p
          className={typographyClass("text-14-medium")}
          style={{ color: "var(--color-text-secondary-v2)", marginTop: "var(--spacing-2)", maxWidth: "62ch" }}
        >
          {t("skills.subtitle")}
        </p>
      </div>

      {/* min(260px, 100%) — на узком экране колонка не должна быть шире карточки. */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(min(260px, 100%), 1fr))", gap: "var(--spacing-3)" }}>
        {SKILLS.map((skill) => (
          // Кнопка лежит рядом с плиткой, а не внутри неё: плитка сама по себе
          // <button>, и вложенная кнопка — невалидная разметка, по которой
          // клавиатура и скринридер расходятся с мышью.
          <div key={skill.id} style={{ position: "relative" }}>
            <Tile
              title={skill.name}
              description={skill.description}
              onClick={() => navigate(skillPath(skill.id))}
              meta={
                <span style={{ display: "flex", flexWrap: "wrap", gap: "var(--spacing-1-5)", marginTop: "var(--spacing-2)" }}>
                  <Badge tone="grey" size="small">{skill.id}</Badge>
                </span>
              }
            />
            {/* Низ карточки, а не верх: сверху заголовок и описание, и кнопка
                перекрывала бы их у навыка с длинным именем. */}
            <span style={{ position: "absolute", bottom: "var(--spacing-2)", right: "var(--spacing-2)" }}>
              <Button
                size="small"
                variant="ghost"
                startIcon={<CopyIcon />}
                onClick={() => copyMarkdown(skill.id)}
                onPointerEnter={() => prefetchMarkdown(skill.id)}
                onFocus={() => prefetchMarkdown(skill.id)}
                aria-label={t("skills.copyMarkdownFor", { name: skill.name })}
              >
                {copiedId === skill.id ? "✓" : t("skills.copyMarkdown")}
              </Button>
            </span>
          </div>
        ))}
      </div>

      {SKILLS.length === 0 && (
        <p className={typographyClass("text-14-medium")} style={{ color: "var(--color-text-secondary-v2)" }}>
          {t("skills.empty")}
        </p>
      )}
    </Card>
  );
}
